import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../hooks/useFetch";
import logo from "../../assets/logo128x128.png";
import QRCode from "react-qr-code";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import PDOnboarding from "./PDOnboarding";
import PDPartnerCertificate from "./PDPartnerCertificate";
import PDPartnerAgreement from "./PDPartnerAgreement";
import WebpImgUploader from "./modules/components/WebpImgUploader";

const ROLE_OPTIONS = [
  "STUDENT",
  "PARENT",
  "MENTOR",
  "INSTITUTION",
  "DIRECTOR",
  "COORDINATOR",
  "INFLUENCER",
  "MERCHANT",
  "FOUNDHER",
  "MARKETER",
];

const PDOnboardingProfile = ({ t, selectedLanguage, role }) => {
  const authState = useSelector((state) => state.authReducer || {});
  const [fetchData] = useFetch();
  const userId = authState?.user?._id || null;
  const userCode = authState?.user?.think5whyscode || "";
  const userName = authState?.user?.name || "";
  const token = authState?.token;
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [msg, setMsg] = useState("");

  const [profileImageError, setProfileImageError] = useState(false);
  const [showUploader, setShowUploader] = useState(false);
  // const profilePhotoUrl = form?.profilePhoto
  //   ? `/api/downloads/users/profilepic/${userCode}.webp`
  //   : "";

  const [isUpdated, setIsUpdated] = useState(false);
  // const [showHustler, setShowHustler] = useState(false);
  const [showExtraInputs, setShowExtraInputs] = useState(false);
  const [isOnboarded, setIsOnboarded] = useState(false);
  const [activeRoleTab, setActiveRoleTab] = useState(null);
  const [selectedRolePrint, setSelectedRolePrint] = useState(null);
  const [selectedRoleAgreement, setSelectedRoleAgreement] = useState(null);
  const isEditMode = Boolean(editId);
  const isReadOnly = isEditMode;
  const [isEditing, setIsEditing] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);

  const [mappedUserVerified, setMappedUserVerified] = useState(false);
  const [mappedUserName, setMappedUserName] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [verificationResults, setVerificationResults] = useState({});

  const [loadingStatus, setLoadingStatus] = useState(false);

  const [showCertificate, setShowCertificate] = React.useState(false);

  const specialRoles = ["DIRECTOR", "COORDINATOR", "INFLUENCER"];

  const [coordinators, setCoordinators] = useState([]);
  const [influencers, setInfluencers] = useState([]);
  const [statusMessage, setStatusMessage] = useState("");
  const [showCoordinatorStatus, setShowCoordinatorStatus] = useState(false);
  const [showInfluencerStatus, setShowInfluencerStatus] = useState(false);

  const hasSpecialRole = form?.roles?.some((role) =>
    specialRoles.includes(role),
  );

  const [fieldErrors, setFieldErrors] = useState({});
  const [activeField, setActiveField] = useState("");

  const toPercent = (val, fallback = 0) => {
    const n = Number(val ?? fallback);
    return n > 1 ? n : n * 100; // >1 means already stored as percent (bad data)
  };

  const buildFilledForm = (record, userCode, userName) => ({
    userId: record.userId || "",
    userCode: record.userCode || userCode || "",
    userName: record.userName || userName || "",
    mobileNumber: record.mobileNumber || "",
    emailId: record.emailId || "",
    roles: record.roles || [],
    preferredActiveRole: record.preferredActiveRole || "STUDENT",
    isStudent: record.isStudent ?? false,
    isParent: record.isParent ?? false,
    isMentor: record.isMentor ?? false,
    isInstitution: record.isInstitution ?? false,
    isDirector: record.isDirector ?? false,
    isAdmin: record.isAdmin ?? false,
    isCoordinator: record.isCoordinator ?? false,
    isMerchant: record.isMerchant ?? false,
    referredBy: record.referredBy || null,
    referredByCode: record.referredByCode || "",
    referredByName: record.referredByName || "",
    institutionCode: record.institutionCode || "",
    institutionName: record.institutionName || "",
    parentCode: record.parentCode || "",
    parentName: record.parentName || "",
    mentorCode: record.mentorCode || "",
    directorCode: record.directorCode || "",
    coordinatorCode: record.coordinatorCode || "",
    influencerCode: record.influencerCode || "",
    isVerified: record.isVerified || false,
    isActive: record.isActive ?? true,
    remarks: record.remarks || "",
    isAvailable: record.isAvailable || false,
    myTags: Array.isArray(record.myTags) ? record.myTags : [],

    isDirectorActivated: record.isDirectorActivated ?? false,
    isCoordinatorActivated: record.isCoordinatorActivated ?? false,
    isInfluencerActivated: record.isInfluencerActivated ?? false,

    // percentServiceFee: Number(record.percentServiceFee ?? 0.4) * 100,
    // percentDirector: Number(record.percentDirector ?? 0) * 100,
    // percentCoordinator: Number(record.percentCoordinator ?? 0) * 100,
    // percentInfluencer: Number(record.percentInfluencer ?? 0) * 100,
    // percentOthers: Number(record.percentOthers ?? 0) * 100,

    percentServiceFee: toPercent(record.percentServiceFee, 0.4),
    percentDirector: toPercent(record.percentDirector, 0),
    percentCoordinator: toPercent(record.percentCoordinator, 0),
    percentInfluencer: toPercent(record.percentInfluencer, 0),
    percentOthers: toPercent(record.percentOthers, 0),
  });

  const [tagInput, setTagInput] = useState("");
  const addTag = () => {
    const value = tagInput.trim().toLowerCase();
    if (!value) return;
    setForm((prev) => {
      const existing = prev?.myTags || [];
      if (existing.includes(value)) return prev; // prevent duplicates
      return {
        ...prev,
        myTags: [...existing, value],
      };
    });
    setTagInput("");
  };

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTag();
    }
    if (e.key === "Backspace" && !tagInput && form?.myTags?.length) {
      removeTag(form.myTags.length - 1);
    }
  };

  const fetchMyOnboarding = async () => {
    try {
      setLoading(true);
      const res = await fetchData({
        url: "/pd/onboarding/myall",
        method: "post",
        data: { userId },
        headers: { Authorization: token },
      });

      if (res?.status && res.record) {
        // console.log(res.record);
        setForm(buildFilledForm(res.record));
        setIsOnboarded(true);
        setEditId(res.record._id);
      } else {
        setForm(null);
        setIsOnboarded(false);
        setEditId(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) fetchMyOnboarding();
  }, [userId]);

  const selectRole = (r) => {
    if (isReadOnly) return;
    setForm((prev) => ({
      ...prev,
      preferredActiveRole: prev.preferredActiveRole === r ? "STUDENT" : r,
    }));
  };

  // -------------------------
  // Validate TU code format
  // -------------------------
  const validateTUCode = (code) => {
    return /^TU[A-Z0-9]{14}$/.test(code);
  };

  // -------------------------
  // Verify MappedUser
  // -------------------------
  const verifyMappedUser = async (fieldKey, mappedUserCode) => {
    setErrorMsg("");
    setMappedUserVerified(false);
    setMappedUserName("");

    if (!validateTUCode(mappedUserCode)) {
      setErrorMsg("Invalid TU Code (must be 14 chars starting with TU)");
      return;
    }
    setActiveField(fieldKey);
    try {
      const config = {
        url: "/auth/verifyreferralcode",
        method: "post",
        headers: { Authorization: authState.token },
        data: { think5whyscode: mappedUserCode },
      };

      const res = await fetchData(config);

      if (res?.status) {
        setMappedUserName(res.t5wcode.name);
        setMappedUserVerified(true);
      } else {
        setErrorMsg("Mapped User not found");
      }
    } catch (err) {
      setErrorMsg("Mapped User not found");
    }
  };

  const handleUpdate = async () => {
    const errors = {};
    studentMappingFields.forEach((field) => {
      const value = form?.[field.key]?.trim();
      if (field.type === "code" && value) {
        const isValid = value.length === 14 && value.startsWith("T");

        if (!isValid) {
          errors[field.key] =
            `${field.label} must be blank OR exactly 14 characters starting with T`;
        }
      }
    });
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    if (hasDirector === true) {
      const totalAllocation =
        Number(form?.percentDirector || 0) +
        Number(form?.percentCoordinator || 0) +
        Number(form?.percentInfluencer || 0) +
        Number(form?.percentOthers || 0);

      const serviceFee = Number(form?.percentServiceFee || 40);

      if (
        Number(totalAllocation.toFixed(2)) !== Number(serviceFee.toFixed(2))
      ) {
        return setMsg(
          `❌ Total allocation must equal ${serviceFee.toFixed(2)}%`,
        );
      }
    }

    try {
      setLoading(true);
      setMsg("");

      const editablePayload = {
        institutionCode: form.institutionCode,
        institutionName: form.institutionName,
        parentCode: form.parentCode,
        parentName: form.parentName,
        mentorCode: form.mentorCode,
        directorCode: form.directorCode,
        coordinatorCode: form.coordinatorCode,
        influencerCode: form.influencerCode,
        isDirectorActivated: form.isDirectorActivated,
        isCoordinatorActivated: form.isCoordinatorActivated,
        isInfluencerActivated: form.isInfluencerActivated,
        percentDirector: Number(form.percentDirector || 0) / 100,
        percentCoordinator: Number(form.percentCoordinator || 0) / 100,
        percentInfluencer: Number(form.percentInfluencer || 0) / 100,
        percentOthers: Number(form.percentOthers || 0) / 100,
        percentServiceFee: Number(form.percentServiceFee || 40) / 100,
        remarks: form.remarks,
        isMerchant: form.isMerchant,
        isVerified: form.isVerified,
        isActive: form.isActive,
        isAvailable: form.isAvailable,
        myTags: form.myTags,
      };

      const res = await fetchData({
        url: "/pd/onboarding/update",
        method: "put",
        data: editablePayload,
        headers: { Authorization: token },
      });

      if (res?.status) {
        setMsg("✅ Profile updated successfully");
        setIsUpdated(true);

        if (res.record) {
          const normalizedTags = Array.isArray(res.record.myTags)
            ? res.record.myTags
            : typeof res.record.myTags === "string"
              ? res.record.myTags
                  .split(",")
                  .map((t) => t.trim())
                  .filter(Boolean)
              : [];

          setForm({
            ...buildFilledForm(res.record),
            myTags: normalizedTags,
          });
        }
      } else {
        setMsg(res?.msg || "❌ Update failed");
        setIsUpdated(false);
      }
    } catch (err) {
      setMsg("❌ Server error while updating");
      setIsUpdated(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchCoordinatorStatus = async () => {
    setLoadingStatus(true);
    const config = {
      url: "/pd/onboarding/fetch-coordinators",
      method: "post",
      data: {
        think5whyscode: authState.user.think5whyscode,
      },
      headers: { Authorization: authState.token },
    };
    try {
      const response = await fetchData(config);
      if (response.success) {
        setCoordinators(response.data);
        setShowCoordinatorStatus(true);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingStatus(false);
    }
  };

  const fetchInfluencerStatus = async () => {
    setLoadingStatus(true);
    const config = {
      url: "/pd/onboarding/fetch-influencers",
      method: "post",
      data: { think5whyscode: authState.user.think5whyscode },
      headers: { Authorization: authState.token },
    };
    try {
      const response = await fetchData(config);
      if (response.success) {
        setInfluencers(response.data);
        setShowInfluencerStatus(true);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingStatus(false);
    }
  };

  const updateCoordinator = async (row) => {
    const config = {
      url: "/pd/onboarding/update-onboarding-status",
      method: "post",
      data: row,
      headers: { Authorization: authState.token },
    };
    try {
      const response = await fetchData(config);
      setStatusMessage(
        response.success ? "✅ Updated Successfully" : "❌ Update Failed",
      );
    } catch {
      setStatusMessage("❌ Update Failed");
    }

    setTimeout(() => {
      setStatusMessage("");
    }, 3000);
  };

  const updateInfluencer = async (row) => {
    const config = {
      url: "/pd/update-onboarding-status",
      method: "post",
      data: row,
      headers: { Authorization: authState.token },
    };
    try {
      const response = await fetchData(config);
      setStatusMessage(
        response.success ? "✅ Updated Successfully" : "❌ Update Failed",
      );
    } catch {
      setStatusMessage("❌ Update Failed");
    }

    setTimeout(() => {
      setStatusMessage("");
    }, 3000);
  };

  const handleCoordinatorChange = (id, field, value) => {
    setCoordinators((prev) =>
      prev.map((row) =>
        row._id === id
          ? {
              ...row,
              [field]: value,
            }
          : row,
      ),
    );
  };

  const handleInfluencerChange = (id, field, value) => {
    setInfluencers((prev) =>
      prev.map((row) =>
        row._id === id
          ? {
              ...row,
              [field]: value,
            }
          : row,
      ),
    );
  };

  if (loading) {
    return (
      <div className="bg-gray-900 p-5 rounded-xl text-white">
        Loading onboarding...
      </div>
    );
  }

  const Input = ({ label, value, onChange }) => (
    <div className="flex flex-col gap-1">
      <label className="text-xs lg:text-sm text-gray-100">{label}</label>
      <input
        className="w-full bg-gray-700 p-1 rounded-md text-xs lg:text-sm"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );

  const SectionTitle = ({ title }) => (
    <div className="col-span-1 lg:col-span-2 mt-2">
      <h4 className="text-xs lg:text-sm text-cyan-200 font-semibold">
        {title}
      </h4>
    </div>
  );

  const handleCodeChange = (key, value) => {
    const cleaned = String(value ?? "")
      .toUpperCase()
      .replace(/\s/g, "")
      .slice(0, 14);

    const finalValue = cleaned.startsWith("T")
      ? cleaned
      : "T" + cleaned.replace(/^T/i, "");

    setForm((prev) => ({
      ...prev,
      [key]: finalValue,
    }));
  };

  const studentMappingFields = [
    { key: "parentCode", label: "Student Parent Code", type: "code" },
    { key: "mentorCode", label: "Mentor Code", type: "code" },
    { key: "directorCode", label: "Director Code", type: "code" },
    { key: "coordinatorCode", label: "Coordinator Code", type: "code" },
    { key: "influencerCode", label: "Influencer Code", type: "code" },
  ];

  const feeFields = [
    { key: "percentServiceFee", label: "Service Fee on Product Price" },
    { key: "percentDirector", label: "Director Share %" },
    { key: "percentCoordinator", label: "Coordinator Share %" },
    { key: "percentInfluencer", label: "Influencer Share %" },
    { key: "percentOthers", label: "Other Allocation %" },
  ];

  const handleFeeChange = (key, value) => {
    let num = parseFloat(value);

    if (isNaN(num)) num = 0;

    if (num < 0) num = 0;

    if (num > Number(form?.percentServiceFee || 0.4)) {
      num = Number(form?.percentServiceFee || 0.4);
    }

    setForm((prev) => ({
      ...prev,
      [key]: num,
    }));

    setFieldErrors((prev) => ({
      ...prev,
      [key]: "",
    }));
  };

  const pdModule = authState?.user?.modules?.find((m) => m.moduleCode === "PD");

  const isPrivilegedRole =
    pdModule?.roleCodes?.some((role) =>
      ["DIRECTOR", "COORDINATOR", "ADMIN"].includes(
        String(role || "").toUpperCase(),
      ),
    ) || false;

  const roles = form?.roles || [];

  const hasDirector = roles.some(
    (r) => String(r?.roleCode || r).toUpperCase() === "DIRECTOR",
  );

  const hasCoordinator = roles.some(
    (r) => String(r?.roleCode || r).toUpperCase() === "COORDINATOR",
  );

  const hasInfluencer = roles.some(
    (r) => String(r?.roleCode || r).toUpperCase() === "INFLUENCER",
  );

  const hasStudent = roles.some(
    (r) => String(r?.roleCode || r).toUpperCase() === "STUDENT",
  );

  const hasMerchant = roles.some(
    (r) => String(r?.roleCode || r).toUpperCase() === "MERCHANT",
  );

  return (
    <div className="space-y-6 bg-gray-900 p-6 rounded-xl text-white border-t-2 border-cyan-200 mt-2">
      {msg && <div className="text-sm text-green-400">{msg}</div>}
      <div className="flex flex-col gap-6 items-start">
        <div className="text-sm lg:text-lg font-semibold text-cyan-200">Your Profile Photo</div>
        <div className="flex flex-col items-center">
          {!profileImageError ? (
            <img
              src={`/api/downloads/users/profilepic/${userCode}.webp?t=${Date.now()}`}
              alt=""
              onClick={() => setShowUploader(true)}
              onError={(e) => {
                e.currentTarget.style.display = "none";
                setProfileImageError(true);
              }}
              onLoad={() => setProfileImageError(false)}
              className="w-28 h-28 rounded-full border-4 border-white object-cover"
            />
          ) : (
            <div
              onClick={() => setShowUploader(true)}
              className="w-28 h-28 rounded-full bg-white border-4 border-white flex 
              items-center justify-center text-gray-500 text-4xl"
            >
              👤
            </div>
          )}
          <button
            type="button"
            onClick={() => setShowUploader(true)}
            className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-pink-600 hover:bg-pink-700 text-white border-2 border-white"
          >
            ✏️
          </button>
        </div>

        {showUploader && (
          <WebpImgUploader
            label="Change Profile Photo"
            value={`/api/downloads/users/profilepic/${userCode}.webp`}
            rounded={true}
            uploadType="profile"
            uploadUrl="/upload-profilepic"
            token={token}
            maxSizeKB={100}
            showWebpHelp={true}
            onChange={(filename, displayUrl) => {
              setProfileImageError(false);
              setForm((prev) => ({
                ...prev,
                profilePhoto: filename,
              }))
            }}
            onClose={() => setShowUploader(false)}
          />
        )}
      </div>

      <div className="flex flex-col gap-1">
        <h2 className="text-cyan-200 text-sm lg:text-lg mb-2">
          ACTIVE/ASSIGNED ROLES
        </h2>
        {form?.roles?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {form.roles.map((role, index) => {
              const isPreferred = role === form?.preferredActiveRole;
              return (
                <span
                  key={index}
                  className={`px-3 py-1 rounded-lg text-xs lg:text-sm border transition
                      ${
                        isPreferred
                          ? "bg-yellow-400 text-black font-semibold border-yellow-500"
                          : "bg-cyan-700 text-white border-transparent"
                      }`}
                >
                  {role}
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <span
          className={`px-3 py-1 rounded-full text-xs font-semibold ${
            form?.isVerified
              ? "bg-green-900 text-green-300 border border-green-700"
              : "bg-red-900 text-red-300 border border-red-700"
          }`}
        >
          {form?.isVerified ? "✅ Verified" : "❌ Not Verified"}
        </span>

        <span
          className={`px-3 py-1 rounded-full text-xs font-semibold ${
            form?.isActive
              ? "bg-green-900 text-green-300 border border-green-700"
              : "bg-amber-900 text-amber-300 border border-amber-700"
          }`}
        >
          {form?.isActive ? "🟢 Active" : "🟠 Inactive"}
        </span>
      </div>
      {hasSpecialRole && (
        <>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-3 mb-3">
            <h3 className="text-xs font-bold text-gray-300 uppercase tracking-wide mb-2">
              Special Roles
            </h3>

            <div className="flex flex-wrap gap-2">
              {hasDirector && (
                <div className="mt-3">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      form?.isDirectorActivated
                        ? "bg-green-900 text-green-300"
                        : "bg-red-900 text-red-300"
                    }`}
                  >
                    {form?.isDirectorActivated
                      ? "✅ Director Active"
                      : "❌ Director Activation Pending"}
                  </span>

                  {!form?.isDirectorActivated && (
                    <div className="mt-3 p-3 rounded-lg border border-amber-700 bg-amber-950/30">
                      <h4 className="text-amber-300 font-semibold text-sm">
                        👔 Director / Digital Master Director (DMD)
                      </h4>

                      <p className="mt-2 text-xs text-gray-300 leading-relaxed">
                        Director or Digital Master Director (DMD) is a key
                        Franchise and Leadership role within the PurpleDoor
                        ecosystem by Think5Whys. Activation of this role
                        requires a one-time activation fee.
                      </p>

                      <p className="mt-2 text-xs text-gray-300 leading-relaxed">
                        The activation amount is credited to your FuelStash
                        Account as a business advance and can be utilized
                        according to the PurpleDoor Partner Program policies.
                      </p>

                      <p className="mt-2 text-xs text-gray-300 leading-relaxed">
                        To activate your Director role, please subscribe to the
                        appropriate Partner Subscription plan and contact the
                        Sales Team for assistance.
                      </p>

                      <div className="mt-3 text-xs">
                        <div className="text-cyan-300">
                          📧 sales@think5whys.com
                        </div>
                        <div className="text-cyan-300">
                          📧 abhishek@think5whys.com
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {hasCoordinator && (
                <div className="mt-3">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      form?.isCoordinatorActivated
                        ? "bg-green-900 text-green-300"
                        : "bg-amber-900 text-amber-300"
                    }`}
                  >
                    {form?.isCoordinatorActivated
                      ? "✅ Coordinator Active"
                      : "⏳ Coordinator Activation Pending"}
                  </span>

                  {!form?.isCoordinatorActivated && (
                    <div className="mt-3 p-3 rounded-lg border border-cyan-700 bg-cyan-950/20">
                      <h4 className="text-cyan-300 font-semibold text-sm">
                        🤝 Coordinator / Digital Coordinator (DC)
                      </h4>

                      <p className="mt-2 text-xs text-gray-300 leading-relaxed">
                        Coordinator or Digital Coordinator (DC) is an important
                        operational and growth role within the PurpleDoor
                        ecosystem. Coordinators work under the guidance and
                        leadership of Directors and Digital Master Directors
                        (DMDs) to support merchant onboarding, community growth,
                        customer engagement, and business expansion activities.
                      </p>

                      <p className="mt-2 text-xs text-gray-300 leading-relaxed">
                        To activate your Coordinator role, first purchase the
                        applicable PurpleDoor Partner Subscription plan. After
                        subscription, connect with your assigned Director or
                        Digital Master Director (DMD) to complete the activation
                        process.
                      </p>

                      <p className="mt-2 text-xs text-gray-300 leading-relaxed">
                        The activation process includes allocation of working
                        credits into your FuelStash Account, enabling you to
                        participate in PurpleDoor business activities and
                        partner programs.
                      </p>

                      <div className="mt-3 text-xs text-cyan-300">
                        💡 Contact your Director / DMD for activation assistance
                        and onboarding guidance.
                      </div>
                    </div>
                  )}
                </div>
              )}

              {hasInfluencer && (
                <div className="mt-3">
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-900 text-purple-300">
                    📣 Digital Influencer (DI)
                  </span>

                  <div className="mt-3 p-3 rounded-lg border border-purple-700 bg-purple-950/20">
                    <h4 className="text-purple-300 font-semibold text-sm">
                      📣 Digital Influencer (DI)
                    </h4>

                    <p className="mt-2 text-xs text-gray-300 leading-relaxed">
                      Digital Influencers (DIs) play an important role in
                      promoting the PurpleDoor ecosystem, creating awareness,
                      driving referrals, supporting merchant growth, and helping
                      communities discover opportunities within the Think5Whys
                      network.
                    </p>

                    <p className="mt-2 text-xs text-gray-300 leading-relaxed">
                      Unlike Directors (DMDs) and Coordinators (DCs), Digital
                      Influencers are not required to purchase a Partner
                      Subscription or pay any Partnership Activation Fee.
                    </p>

                    <p className="mt-2 text-xs text-gray-300 leading-relaxed">
                      After completing the onboarding process, please contact
                      your Director or Digital Master Director (DMD) to activate
                      your Digital Influencer account and begin participating in
                      PurpleDoor programs and campaigns.
                    </p>

                    <div className="mt-3 text-xs text-purple-300">
                      💡 Contact your Director / DMD for onboarding, account
                      activation, and program participation guidance.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {hasDirector && form?.isActive && form?.isVerified && (
            <>
              <div className="flex flex-col gap-2 border border-cyan-300 p-2 mt-2 mb-2">
                <div className="flex flex-row gap-1 p-2">
                  <button
                    onClick={() => {
                      setShowCoordinatorStatus(true);
                      setShowInfluencerStatus(false);
                      setStatusMessage("");
                      fetchCoordinatorStatus();
                    }}
                    className="px-1 py-1 w-32 rounded-lg bg-cyan-600 text-white text-xs"
                  >
                    Coordinator Status
                  </button>

                  <button
                    onClick={() => {
                      setShowInfluencerStatus(true);
                      setShowCoordinatorStatus(false);
                      setStatusMessage("");
                      fetchInfluencerStatus();
                    }}
                    className="px-1 py-1 w-32 rounded-lg bg-purple-600 text-white text-xs"
                  >
                    Influencer Status
                  </button>

                  <button
                    onClick={() => {
                      setShowCoordinatorStatus(false);
                      setShowInfluencerStatus(false);
                    }}
                    className="px-1 py-1 w-20 rounded-lg bg-red-600 text-white text-xs"
                  >
                    Exit
                  </button>
                </div>
                {showCoordinatorStatus && (
                  <div className="mt-4 rounded-xl border border-cyan-700 bg-slate-900 p-4">
                    <div className="flex justify-between items-center mb-3">
                      <div className="font-semibold text-cyan-300">
                        Coordinator Status
                      </div>
                      <button
                        onClick={() => setShowCoordinatorStatus(false)}
                        className="text-xs w-10 text-red-300"
                      >
                        ✕
                      </button>
                    </div>
                    {statusMessage && (
                      <div className="mb-3 rounded-lg bg-green-900/20 border border-green-500 p-2 text-xs text-green-300">
                        {statusMessage}
                      </div>
                    )}
                    {loadingStatus && (
                      <div className="text-xs text-gray-400 mb-2">
                        Loading...
                      </div>
                    )}
                    <table className="w-full text-xs">
                      <thead>
                        <tr>
                          <th>Code</th>
                          <th>Verified</th>
                          <th>Active</th>
                          <th>Activated</th>
                          <th>Update</th>
                        </tr>
                      </thead>
                      <tbody>
                        {coordinators.map((row) => (
                          <tr key={row._id}>
                            <td>{row.coordinatorCode}</td>
                            <td>
                              <input
                                type="checkbox"
                                checked={row.isVerified}
                                onChange={(e) =>
                                  handleCoordinatorChange(
                                    row._id,
                                    "isVerified",
                                    e.target.checked,
                                  )
                                }
                              />
                            </td>
                            <td>
                              <input
                                type="checkbox"
                                checked={row.isActive}
                                onChange={(e) =>
                                  handleCoordinatorChange(
                                    row._id,
                                    "isActive",
                                    e.target.checked,
                                  )
                                }
                              />
                            </td>
                            <td>
                              <input
                                type="checkbox"
                                checked={row.isCoordinatorActivated}
                                onChange={(e) =>
                                  handleCoordinatorChange(
                                    row._id,
                                    "isCoordinatorActivated",
                                    e.target.checked,
                                  )
                                }
                              />
                            </td>
                            <td>
                              <button
                                onClick={() => updateCoordinator(row)}
                                className="px-2 py-1 rounded bg-cyan-600 text-white"
                              >
                                Save
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {showInfluencerStatus && (
                  <div className="mt-4 rounded-xl border border-cyan-700 bg-slate-900 p-4">
                    <div className="flex justify-between items-center mb-3">
                      <div className="font-semibold text-cyan-300">
                        Influencer Status
                      </div>
                      <button
                        onClick={() => setShowInfluencerStatus(false)}
                        className="text-xs w-10 text-red-300"
                      >
                        ✕
                      </button>
                    </div>
                    {statusMessage && (
                      <div className="mb-3 rounded-lg bg-green-900/20 border border-green-500 p-2 text-xs text-green-300">
                        {statusMessage}
                      </div>
                    )}
                    {loadingStatus && (
                      <div className="text-xs text-gray-400 mb-2">
                        Loading...
                      </div>
                    )}
                    <table className="w-full text-xs">
                      <thead>
                        <tr>
                          <th>Code</th>
                          <th>Verfied</th>
                          <th>Active</th>
                          <th>Activated</th>
                          <th>Update</th>
                        </tr>
                      </thead>
                      <tbody>
                        {influencers.map((row) => (
                          <tr key={row._id}>
                            <td>{row.influencerCode}</td>
                            <td>
                              <input
                                type="checkbox"
                                checked={row.isVerified}
                                onChange={(e) =>
                                  handleInfluencerChange(
                                    row._id,
                                    "isVerified",
                                    e.target.checked,
                                  )
                                }
                              />
                            </td>
                            <td>
                              <input
                                type="checkbox"
                                checked={row.isActive}
                                onChange={(e) =>
                                  handleInfluencerChange(
                                    row._id,
                                    "isActive",
                                    e.target.checked,
                                  )
                                }
                              />
                            </td>
                            <td>
                              <input
                                type="checkbox"
                                checked={row.isInfluencerActivated}
                                onChange={(e) =>
                                  handleInfluencerChange(
                                    row._id,
                                    "isInfluencerActivated",
                                    e.target.checked,
                                  )
                                }
                              />
                            </td>
                            <td>
                              <button
                                onClick={() => updateInfluencer(row)}
                                className="px-2 py-1 rounded bg-cyan-600 text-white"
                              >
                                Save
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {form?.isActive && form?.isVerified ? (
            <PDPartnerCertificate
              form={form}
              hasDirector={hasDirector}
              hasCoordinator={hasCoordinator}
              hasInfluencer={hasInfluencer}
            />
          ) : (
            <div className="mt-4 rounded-lg border border-red-800 bg-red-950/20 p-4 text-center">
              <div className="text-red-300 font-semibold">
                🔒 Certificate & Agreement Locked
              </div>

              <div className="mt-2 text-xs text-gray-300">
                Please get your account verified and activated before you can
                view, download, print, or share your Partner Certificate and
                Partnership Agreement.
              </div>

              <div className="mt-3 text-cyan-300 text-xs">
                Contact your supervisor or Director / DMD to complete the
                activation process.
              </div>
            </div>
          )}
        </>
      )}

      <div className="flex flex-col gap-1">
        <h2 className="text-yellow-200">YOU CAN ALSO AQUIRE REMAINING ROLES</h2>

        <div className="flex flex-row flex-wrap gap-2 mt-2">
          {ROLE_OPTIONS.filter((role) => !form?.roles?.includes(role)).map(
            (role, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setActiveRoleTab(role)}
                className="w-fit px-3 py-1 rounded-lg text-xs lg:text-sm border border-gray-600 bg-teal-800 text-gray-300 hover:bg-cyan-700 hover:text-white transition"
              >
                {role}
              </button>
            ),
          )}
        </div>
      </div>

      {activeRoleTab && (
        <PDOnboarding
          t={t}
          selectedLanguage={selectedLanguage}
          role={activeRoleTab}
          isNew={false}
        />
      )}

      <div>
        <p className="text-xs lg:text-sm text-gray-100 mb-2">
          Profile Name: {authState?.user?.name}
        </p>
        <p className="text-xs lg:text-sm text-gray-100">
          Has Approver Role: {isPrivilegedRole ? "Yes" : "No"}
        </p>
        <p className="text-xs lg:text-sm text-gray-100">
          Mobile Number: {form?.mobileNumber}
        </p>
        <p className="text-xs lg:text-sm text-gray-100">
          Email ID: {form?.emailId}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-2">
        <p className="text-gray-100 text-xs lg:text-sm">
          Referred BY: {form?.referredByName} {" ("}
          {form?.referredByCode}
          {")"}
        </p>
      </div>

      <div className="bg-gray-800 p-4 rounded-xl space-y-2 border-t border-yellow-100">
        <h3 className="text-cyan-300 text-sm lg:text-lg font-semibold">
          Additional Details
        </h3>
        <div className="flex flex-col gap-1">
          <button
            onClick={() => setIsEditing(!isEditing)}
            disabled={isEditing}
            className="w-fit text-xs lg:text-sm bg-purple-600 hover:bg-purple-800 px-2 py-1 rounded-md font-medium"
          >
            Click here to enable Edit Profile
          </button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-2">
          <div className="flex flex-col gap-1">
            <label className="text-xs lg:text-sm text-gray-100">
              Institution Code
            </label>
            <input
              className="bg-gray-700 p-1 rounded-md w-[280px] tracking-widest text-xs lg:text-sm"
              value={form?.institutionCode}
              disabled={
                form?.isVerified === true || !(hasDirector || hasCoordinator)
              }
              onChange={(e) =>
                setForm({ ...form, institutionCode: e.target.value })
              }
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs lg:text-sm text-gray-100">
              Institution Name
            </label>
            <input
              className="w-full bg-gray-700 p-1 rounded-md text-xs lg:text-sm"
              value={form?.institutionName}
              disabled={form?.isVerified === true}
              onChange={(e) =>
                setForm({ ...form, institutionName: e.target.value })
              }
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-2 border-t border-yellow-100"></div>
        <h4 className="text-sm lg:text-lg text-cyan-200 font-semibold mb-2">
          Onboarding Relationship Mapping
        </h4>
        <div className="col-span-1 lg:col-span-2">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-2">
            {studentMappingFields.map((field) => (
              <div key={field.key} className="flex flex-col gap-1">
                <label className="text-xs lg:text-sm text-gray-100">
                  {field.label}
                </label>

                <input
                  maxLength={14}
                  disabled={
                    form?.isVerified === true ||
                    (["parentCode", "mentorCode"].includes(field.key) &&
                      !hasStudent)
                  }
                  className={`bg-gray-700 p-1 rounded-md w-[280px] tracking-widest text-xs lg:text-sm border ${
                    fieldErrors[field.key]
                      ? "border-red-500"
                      : "border-gray-600"
                  }`}
                  value={form?.[field.key] || ""}
                  onChange={(e) => {
                    const value = e.target.value;

                    handleCodeChange(field.key, value);
                    setFieldErrors((prev) => ({
                      ...prev,
                      [field.key]: "",
                    }));
                  }}
                />
                {/* <p className={mappedUserVerified ? "text-green-400" : "text-red-400"}>
                      {mappedUserVerified ? mappedUserName : errorMsg}
                    </p> */}
                {activeField === field.key && (
                  <p className="text-red-400 text-xs">{errorMsg}</p>
                )}
                {activeField === field.key && mappedUserVerified && (
                  <p className="text-green-400 text-xs">{mappedUserName}</p>
                )}
                <button
                  onClick={() => verifyMappedUser(field.key, form?.[field.key])}
                  disabled={
                    form?.isVerified === true ||
                    (["parentCode", "mentorCode"].includes(field.key) &&
                      !hasStudent)
                  }
                  className="w-fit px-4 mt-2 bg-blue-600 hover:bg-blue-700 text-white py-1 rounded-lg text-xs lg:text-sm"
                >
                  Verify Mapped User
                </button>

                {fieldErrors[field.key] && (
                  <p className="text-red-400 text-xs">
                    {fieldErrors[field.key]}
                  </p>
                )}
              </div>
            ))}
          </div>
          <p>{role}</p>
        </div>

        {hasDirector && (
          <div className="border-t border-yellow-100 pt-3 mt-2">
            <h4 className="text-sm lg:text-lg text-cyan-200 font-semibold mb-4">
              Fee & Allocation (Director / DMD View)
            </h4>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-xs lg:text-sm text-gray-100">
                  Service Fee (%) on Product Price
                </label>

                <input
                  disabled
                  value={Number(form?.percentServiceFee ?? 40).toFixed(2)}
                  className="bg-gray-700 p-2 rounded-md w-[280px] text-xs lg:text-sm border border-gray-600 opacity-70 cursor-not-allowed"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs lg:text-sm text-gray-100">
                  Allocation Amount
                </label>
                <label className="text-xs lg:text-sm text-gray-100">
                  Director / DMD (%)
                </label>

                <input
                  type="text"
                  inputMode="decimal"
                  value={form?.percentDirector ?? ""}
                  onChange={(e) => {
                    const value = e.target.value.trim();

                    // allow only numbers and decimal
                    if (!/^\d*\.?\d*$/.test(value)) return;

                    const num = Number(value);

                    if (value !== "" && (num < 0 || num > 40)) return;

                    setForm((prev) => ({
                      ...prev,
                      percentDirector: value,
                    }));
                  }}
                  className="bg-gray-700 p-2 rounded-md w-[280px] text-xs lg:text-sm border border-gray-600"
                  placeholder="0 - 40"
                />
                <label className="text-xs lg:text-sm text-gray-100">
                  Coordinator / DC (%)
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={form?.percentCoordinator ?? ""}
                  onChange={(e) => {
                    const value = e.target.value.trim();

                    // allow only numbers and decimal
                    if (!/^\d*\.?\d*$/.test(value)) return;

                    const num = Number(value);

                    if (value !== "" && (num < 0 || num > 40)) return;

                    setForm((prev) => ({
                      ...prev,
                      percentCoordinator: value,
                    }));
                  }}
                  className="bg-gray-700 p-2 rounded-md w-[280px] text-xs lg:text-sm border border-gray-600"
                  placeholder="0 - 40"
                />
                <label className="text-xs lg:text-sm text-gray-100">
                  Influencer/ DI (%)
                </label>

                <input
                  type="text"
                  inputMode="decimal"
                  value={form?.percentInfluencer ?? ""}
                  onChange={(e) => {
                    const value = e.target.value.trim();

                    // allow only numbers and decimal
                    if (!/^\d*\.?\d*$/.test(value)) return;

                    const num = Number(value);

                    if (value !== "" && (num < 0 || num > 40)) return;

                    setForm((prev) => ({
                      ...prev,
                      percentInfluencer: value,
                    }));
                  }}
                  className="bg-gray-700 p-2 rounded-md w-[280px] text-xs lg:text-sm border border-gray-600"
                  placeholder="0 - 40"
                />
                <label className="text-xs lg:text-sm text-gray-100">
                  Misc / Others (%)
                </label>

                <input
                  type="text"
                  inputMode="decimal"
                  value={form?.percentOthers ?? ""}
                  onChange={(e) => {
                    const value = e.target.value.trim();

                    // allow only numbers and decimal
                    if (!/^\d*\.?\d*$/.test(value)) return;

                    const num = Number(value);

                    if (value !== "" && (num < 0 || num > 40)) return;

                    setForm((prev) => ({
                      ...prev,
                      percentOthers: value,
                    }));
                  }}
                  className="bg-gray-700 p-2 rounded-md w-[280px] text-xs lg:text-sm border border-gray-600"
                  placeholder="0 - 40"
                />
              </div>
            </div>

            <div className="mt-3 text-yellow-300 text-sm">
              Allocation:{" "}
              {(
                Number(form?.percentDirector ?? 0.0) +
                Number(form?.percentCoordinator ?? 0.0) +
                Number(form?.percentInfluencer ?? 0.0) +
                Number(form?.percentOthers ?? 0.0)
              ).toFixed(2)}
              {" / "}
              {Number(form?.percentServiceFee ?? 40.0).toFixed(2)}%
            </div>
          </div>
        )}
        <div className="mt-2 p-2 border-t border-yellow-100">
          <h4 className="text-sm lg:text-lg text-cyan-200 font-semibold mb-2">
            Profile Status
          </h4>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-2">
            <div className="flex flex-col gap-1">
              <label className="text-xs lg:text-sm text-gray-100">
                Is Active
              </label>
              <select
                disabled={!isPrivilegedRole}
                className="bg-gray-700 p-1 rounded-md text-xs lg:text-sm"
                value={form?.isActive ? "true" : "false"}
                onChange={(e) =>
                  setForm({ ...form, isActive: e.target.value === "true" })
                }
              >
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs lg:text-sm text-gray-100">
                Verification Status {role}{" "}
              </label>
              <select
                className="bg-gray-700 p-1 rounded-md text-xs lg:text-sm"
                disabled={hasDirector}
                value={String(form?.isVerified)}
                onChange={(e) =>
                  setForm({
                    ...form,
                    isVerified: e.target.value === "true",
                  })
                }
              >
                <option value="true">Verified</option>
                <option value="false">Unverified</option>
              </select>
              {hasDirector && role === "director" && (
                <p className="text-xs text-amber-400 mt-1">
                  Note: Director verification is handled by Admin.
                </p>
              )}
              {hasCoordinator && role === "coordinator" && (
                <p className="text-xs text-amber-400 mt-1">
                  Note: Coordinator verification is handled by Director.
                </p>
              )}
              {hasInfluencer && role === "influencer" && (
                <p className="text-xs text-amber-400 mt-1">
                  Note: Influencer verification is handled by Director.
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs lg:text-sm text-gray-100">
                Is Merchant
              </label>
              <select
                disabled={!(hasDirector || hasCoordinator)}
                className="bg-gray-700 p-1 rounded-md text-xs lg:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                value={String(form?.isMerchant)}
                onChange={(e) =>
                  setForm({ ...form, isMerchant: e.target.value === "true" })
                }
              >
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div className="col-span-1 lg:col-span-2 gap-1">
              <label className="text-xs lg:text-sm text-gray-100">
                Remarks
              </label>
              <textarea
                className="w-full bg-gray-700 p-1 text-xs rounded-md"
                style={{ height: "150px" }}
                value={form?.remarks}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
              />
            </div>
          </div>
        </div>
      </div>

      {msg && <div className="text-xs lg:text-sm text-yellow-300">{msg}</div>}
      {Object.keys(fieldErrors).length > 0 && (
        <div className="text-red-400 text-xs lg:text-sm mt-2 text-center">
          ⚠️ View and correct all errors before updating!
        </div>
      )}
      <div className="flex flex-row items-center gap-2">
        <div className="flex flex-row w-full gap-1">
          <button
            onClick={handleUpdate}
            disabled={isUpdated || !isEditing || isCancelled}
            className="w-1/3 text-xs lg:text-sm bg-teal-600 hover:bg-teal-800 px-2 py-1 rounded-md font-medium disabled:opacity-50"
          >
            Update Profile
          </button>

          <button
            onClick={() => setIsEditing(false)}
            className="w-1/3 text-xs lg:text-sm bg-gray-600 hover:bg-gray-800 px-2 py-1 rounded-md font-medium"
          >
            Cancel Editing
          </button>
        </div>
      </div>
    </div>
  );
};

export default PDOnboardingProfile;
